// Drill region overlay + bounds helpers.
//
// Lifted out of `apps/ui/coding/src/session-replay/components/PitchCanvas.tsx`
// so the reporting player view can render the same dashed-region + zoom-in
// behaviour when a drill is selected. Keeps a single drawing code path across
// the coding tool (live playback) and the reporting tool (static snapshot).
import { STRACK_COLOURS } from '@frontend/strack'
import type { PitchBounds, PitchTransform } from './types'

export interface DrillRegionPoint {
  x: number
  y: number
  z: number
}

// Keyed by corner id. P1..P4 are the four rectangle corners (required for the
// overlay to render). P5..P8 are optional goalpost anchors for
// rugby / American Football.
export type DrillRegion = Record<string, DrillRegionPoint>

const RECT_CORNERS = ['P1', 'P2', 'P3', 'P4'] as const
const GOAL_POSTS: [string, string][] = [
  ['P5', 'P7'],
  ['P6', 'P8']
]

/**
 * Axis-aligned bounding box of the P1..P4 rectangle in world (meters)
 * coordinates. Feed into `usePitchCanvas({ focusBounds })` to trigger the
 * existing lerp zoom animation. Returns `null` when the region doesn't
 * carry the four canonical corners — caller should skip the focus.
 */
export function computeDrillBounds(region: DrillRegion): PitchBounds | null {
  const pts = RECT_CORNERS.map((k) => region[k]).filter(
    (p): p is DrillRegionPoint => Boolean(p)
  )
  if (pts.length < 4) return null
  const xs = pts.map((p) => p.x)
  const ys = pts.map((p) => p.y)
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys)
  }
}

/**
 * Draws the drill zone: dashed amber rectangle (P1→P2→P3→P4) with a
 * semi-transparent fill, plus red goalpost lines/dots for P5↔P7 and
 * P6↔P8 when those corners are present and above ground level.
 *
 * No-ops when the four rectangle corners aren't all present — callers
 * can safely pass partial regions.
 *
 * Expected draw order: AFTER the base pitch, BEFORE player markers, so
 * the overlay sits on the pitch but doesn't obscure action points.
 */
export function drawDrillRegion(
  ctx: CanvasRenderingContext2D,
  region: DrillRegion,
  transform: PitchTransform
): void {
  const r = region
  if (!r.P1 || !r.P2 || !r.P3 || !r.P4) return

  const { worldToCanvas } = transform
  const c1 = worldToCanvas(r.P1.x, r.P1.y)
  const c2 = worldToCanvas(r.P2.x, r.P2.y)
  const c3 = worldToCanvas(r.P3.x, r.P3.y)
  const c4 = worldToCanvas(r.P4.x, r.P4.y)

  ctx.save()
  ctx.setLineDash([8, 4])
  ctx.strokeStyle = STRACK_COLOURS.drill.region
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(c1.cx, c1.cy)
  ctx.lineTo(c2.cx, c2.cy)
  ctx.lineTo(c3.cx, c3.cy)
  ctx.lineTo(c4.cx, c4.cy)
  ctx.closePath()
  ctx.stroke()

  ctx.fillStyle = STRACK_COLOURS.drill.regionFill
  ctx.fill()
  ctx.setLineDash([])
  ctx.restore()

  // Goalposts: only drawn when the paired anchors exist and at least one
  // sits above ground level (z > 0). Rendered on top of the dashed
  // rectangle, within the same save/restore block.
  for (const [a, b] of GOAL_POSTS) {
    const pa = r[a]
    const pb = r[b]
    if (!pa || !pb || (pa.z <= 0 && pb.z <= 0)) continue

    const ca = worldToCanvas(pa.x, pa.y)
    const cb = worldToCanvas(pb.x, pb.y)

    ctx.beginPath()
    ctx.moveTo(ca.cx, ca.cy)
    ctx.lineTo(cb.cx, cb.cy)
    ctx.strokeStyle = STRACK_COLOURS.drill.goalPost
    ctx.lineWidth = 3
    ctx.stroke()

    for (const cp of [ca, cb]) {
      ctx.beginPath()
      ctx.arc(cp.cx, cp.cy, 4, 0, Math.PI * 2)
      ctx.fillStyle = STRACK_COLOURS.drill.goalPost
      ctx.fill()
    }
  }
}
